import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { FileText, Plus, Trash2, X, Eye, Edit3, Calendar, Search, MessageCircle, Mail } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button, Input, Card, Badge, Spinner } from "@/components/kit";

const VARS = ["{nama_bisnis}", "{kota}", "{kategori}", "{website}", "{rating}", "{nama_user}"];
const SAMPLE = { "{nama_bisnis}": "Restoran Makmur", "{kota}": "Jakarta", "{kategori}": "Restoran", "{website}": "makmur.co.id", "{rating}": "4.5", "{nama_user}": "Budi" };
const EMPTY_FORM = { name: "", content: "", type: "wa", subject: "" };

const previewText = (content) => {
  let c = content || "";
  VARS.forEach((v) => (c = c.replaceAll(v, SAMPLE[v])));
  return c;
};
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export default function Templates() {
  const [templates, setTemplates] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null → create mode
  const [viewing, setViewing] = useState(null); // template being inspected in read-only mode
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | wa | email

  const load = useCallback(async () => {
    try { const { data } = await api.get("/templates"); setTemplates(data.templates); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!templates) return [];
    const q = search.trim().toLowerCase();
    return templates.filter((t) => {
      if (filter !== "all" && (t.type || "wa") !== filter) return false;
      if (!q) return true;
      return (t.name + " " + (t.subject || "") + " " + t.content).toLowerCase().includes(q);
    });
  }, [templates, search, filter]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setEditorOpen(true); };
  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, content: t.content, type: t.type || "wa", subject: t.subject || "" });
    setViewing(null);
    setEditorOpen(true);
  };
  const closeEditor = () => { setEditorOpen(false); setEditingId(null); setForm(EMPTY_FORM); };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error("Nama template wajib diisi"); return; }
    if (!form.content.trim()) { toast.error("Isi template wajib diisi"); return; }
    if (form.type === "email" && !form.subject.trim()) { toast.error("Subjek email wajib diisi"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/templates/${editingId}`, form);
        toast.success("Template diperbarui");
      } else {
        await api.post("/templates", form);
        toast.success("Template disimpan");
      }
      closeEditor();
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus template ini? Tindakan ini tidak bisa dibatalkan.")) return;
    try { await api.delete(`/templates/${id}`); toast.success("Template dihapus"); setViewing(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const insertVar = (v) => setForm((f) => ({ ...f, content: (f.content ? f.content + " " : "") + v }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-sky-50 grid place-items-center"><FileText className="w-6 h-6 text-sky-600" /></div>
          <div>
            <h1 className="font-display text-3xl font-extrabold">Template Pesan</h1>
            <p className="text-slate-500 mt-1">Klik kartu untuk lihat detail; klik Edit untuk ubah isinya.</p>
          </div>
        </div>
        <Button data-testid="add-template-btn" onClick={openCreate}><Plus className="w-4 h-4" /> Template Baru</Button>
      </div>

      {/* Toolbar */}
      {templates && templates.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
            <input
              data-testid="template-search"
              type="text"
              placeholder="Cari template..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-slate-200 pl-10 pr-3 py-2 text-sm focus:border-sky-300 outline-none"
            />
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
            {[["all", "Semua"], ["wa", "WA"], ["email", "Email"]].map(([v, l]) => (
              <button key={v} data-testid={`template-filter-${v}`} onClick={() => setFilter(v)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${filter === v ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                {l}
              </button>
            ))}
          </div>
          <span className="text-xs text-slate-400 ml-auto">{filtered.length} dari {templates.length} template</span>
        </div>
      )}

      {/* Grid */}
      {templates === null ? <div className="flex justify-center py-20"><Spinner className="w-7 h-7" /></div> :
        templates.length === 0 ? (
          <Card className="p-12 text-center text-slate-400">
            <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            Belum ada template. Buat satu untuk mulai blast.
          </Card>
        ) : filtered.length === 0 ? (
          <Card className="p-10 text-center text-slate-400">Tidak ada template yang cocok dengan pencarian.</Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((t) => (
              <Card key={t.id}
                onClick={() => setViewing(t)}
                className="p-5 flex flex-col cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition group"
                data-testid="template-card">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-slate-800 line-clamp-1">{t.name}</h3>
                  <Badge color={t.type === "email" ? "violet" : "sky"}>
                    {t.type === "email" ? <><Mail className="w-3 h-3" /> Email</> : <><MessageCircle className="w-3 h-3" /> WA</>}
                  </Badge>
                </div>
                {t.type === "email" && t.subject && (
                  <p className="text-xs text-violet-600 mt-1.5 truncate" title={t.subject}>Subjek: {t.subject}</p>
                )}
                <p className="text-sm text-slate-500 mt-2 flex-1 whitespace-pre-wrap line-clamp-4">{t.content}</p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-50">
                  <Badge color="slate">Dipakai {t.use_count}x</Badge>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button data-testid="template-edit-btn" onClick={(e) => { e.stopPropagation(); openEdit(t); }}
                      className="rounded-lg p-1.5 text-slate-400 hover:text-sky-600 hover:bg-sky-50" title="Edit">
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button data-testid="template-delete-btn" onClick={(e) => { e.stopPropagation(); remove(t.id); }}
                      className="rounded-lg p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50" title="Hapus">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

      {/* View modal (read-only with Edit/Delete actions) */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="template-view-modal">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setViewing(null)} />
          <Card className="relative w-full max-w-2xl p-6 animate-float-up max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge color={viewing.type === "email" ? "violet" : "sky"}>
                    {viewing.type === "email" ? <><Mail className="w-3 h-3" /> Email</> : <><MessageCircle className="w-3 h-3" /> WA</>}
                  </Badge>
                  <Badge color="slate">Dipakai {viewing.use_count}x</Badge>
                </div>
                <h2 className="font-display font-bold text-xl mt-2">{viewing.name}</h2>
                <div className="text-xs text-slate-400 mt-1 flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" /> Dibuat: {fmtDate(viewing.created_at)}
                  {viewing.updated_at && <span className="ml-2">· Diubah: {fmtDate(viewing.updated_at)}</span>}
                </div>
              </div>
              <button onClick={() => setViewing(null)} data-testid="template-view-close"><X className="w-5 h-5 text-slate-400 hover:text-slate-700" /></button>
            </div>

            {viewing.type === "email" && (
              <div className="rounded-xl bg-violet-50 border border-violet-200 px-4 py-3 mb-3">
                <p className="text-xs text-violet-500 font-semibold mb-0.5">Subjek</p>
                <p className="text-sm text-slate-700">{viewing.subject || "—"}</p>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1.5">Isi pesan</p>
              <div className={`rounded-xl border px-4 py-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed ${viewing.type === "email" ? "bg-violet-50/50 border-violet-100" : "bg-emerald-50 border-emerald-100"}`}>
                {viewing.content}
              </div>
            </div>

            <div className="mt-4">
              <p className="text-xs font-semibold text-slate-500 mb-1.5 flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Preview (variabel diganti contoh)</p>
              <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                {viewing.type === "email" && viewing.subject && (
                  <p className="font-semibold mb-1">{previewText(viewing.subject)}</p>
                )}
                {previewText(viewing.content)}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 mt-5 pt-4 border-t border-slate-100">
              <Button data-testid="template-view-delete" variant="danger" size="sm" onClick={() => remove(viewing.id)}>
                <Trash2 className="w-4 h-4" /> Hapus
              </Button>
              <Button data-testid="template-view-edit" onClick={() => openEdit(viewing)}>
                <Edit3 className="w-4 h-4" /> Edit Template
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Editor modal (create + edit) */}
      {editorOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="template-editor-modal">
          <div className="absolute inset-0 bg-slate-900/40" onClick={closeEditor} />
          <Card className="relative w-full max-w-2xl p-6 animate-float-up max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display font-bold text-lg">
                {editingId ? <><Edit3 className="w-5 h-5 text-sky-500 inline mr-1" /> Edit Template</> : "Template Baru"}
              </h2>
              <button onClick={closeEditor} data-testid="template-editor-close"><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Nama template</label>
                <Input data-testid="template-name" required placeholder="Outreach Restoran Jakarta" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Tipe</label>
                <div className="flex gap-2">
                  {[["wa", "WhatsApp", MessageCircle, "sky"], ["email", "Email", Mail, "violet"]].map(([v, label, Ic, c]) => (
                    <button type="button" key={v} data-testid={`template-type-${v}`} onClick={() => setForm({ ...form, type: v })}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition flex items-center justify-center gap-2 ${
                        form.type === v
                          ? c === "violet" ? "border-violet-400 bg-violet-50 text-violet-700" : "border-sky-400 bg-sky-50 text-sky-700"
                          : "border-slate-200 text-slate-500 hover:border-slate-300"
                      }`}>
                      <Ic className="w-4 h-4" /> {label}
                    </button>
                  ))}
                </div>
              </div>
              {form.type === "email" && (
                <div>
                  <label className="text-xs font-semibold text-slate-500 mb-1 block">Subjek email</label>
                  <Input data-testid="template-subject" required placeholder="Mau tingkatkan penjualan {nama_bisnis}?" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Isi pesan</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {VARS.map((v) => <button type="button" key={v} onClick={() => insertVar(v)} className="text-xs bg-sky-50 text-sky-700 px-2 py-1 rounded-lg hover:bg-sky-100">{v}</button>)}
                </div>
                <textarea data-testid="template-content" required rows={6} placeholder="Halo {nama_bisnis}! Kami lihat usaha {kategori} Anda di {kota}..."
                  value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none" />
              </div>
              {form.content && (
                <div className="bg-slate-50 rounded-xl p-3 text-sm text-slate-600">
                  <p className="text-xs font-semibold text-slate-400 mb-1 flex items-center gap-1"><Eye className="w-3 h-3" /> Preview</p>
                  {form.type === "email" && form.subject && (
                    <p className="font-semibold mb-1 text-slate-700">{previewText(form.subject)}</p>
                  )}
                  <span className="whitespace-pre-wrap">{previewText(form.content)}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-2 pt-2">
                <Button type="button" variant="outline" onClick={closeEditor}>Batal</Button>
                <Button data-testid="template-submit" type="submit" loading={saving}>
                  {editingId ? "Simpan Perubahan" : "Simpan Template"}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
